import MembershipCard from "./MembershipCard";
import type { MembershipMember, MembershipStatus } from "../types";

interface HouseholdMemberListProps {
  members: MembershipMember[];
  membershipType: string;
  membershipStatus: MembershipStatus;
}

/**
 * @component HouseholdMemberList
 * @description Scrollable list of membership cards for all members under a selected membership.
 * Renders a primary card (expanded by default) followed by household member cards.
 * Supports horizontal scrolling on mobile and vertical layout on desktop.
 * Returns null when the members array is empty.
 * @param members - Array of membership members; the first primary member is rendered expanded.
 * @param membershipType - Membership tier label passed to each card.
 * @param membershipStatus - Current membership status passed to each card.
 * @returns A scrollable `<ul>` of `MembershipCard` elements, or null if empty.
 */
export default function HouseholdMemberList({
  members,
  membershipType,
  membershipStatus,
}: HouseholdMemberListProps) {
  if (members.length === 0) return null;

  return (
    <ul
      role="list"
      aria-label="Household members"
      className="flex lg:flex-col gap-4 w-full overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0"
    >
      {members.map((member, index) => (
        <li key={(member.id ?? "") + index} className="flex-shrink-0 w-[85vw] lg:w-full flex flex-col">
          <MembershipCard
            memberId={member.id}
            memberName={member.name}
            memberSince={member.memberSince}
            expires={member.expires}
            isPrimary={member.isPrimary ?? false}
            membershipType={membershipType}
            membershipStatus={membershipStatus}
          />
        </li>
      ))}
    </ul>
  );
}
